import * as React from 'react';

import { Box } from '@mui/material';
import { useState, useEffect } from 'react';
import removeAndFetch, { checkPokemonChange } from '../services/getPokemon';
import { Pkmn } from '../data/pkmnList';
import { WinnerLetter } from './winnerLetter';
import { PokemonInfo } from './pokemonInfo';
import { getDate } from '../services/getDate';
import { getPokemonInfoFromStorage } from '../services/getPokemonInfoFromStorage';
import { getPokemonFromStorage } from '../services/getPokemonFromStorage';

import {
  compareAbilities,
  compareBothTypes,
  compareGeneration,
  compareHeight,
  compareTotalStats,
  compareWeight,
  returnArrow,
} from '../utils/compares';
import '../assets/animations.css';
import InfoText from './infoText';
import { getGeneration } from '../services/getGen';
import PokemonAutocomplete from './pokemonAutoComplete';

{
  /* AUTOCOMPLETE INPUT */
}

function InputPokemons() {
  const [pokemonInfoList, setPokemonInfoList] = useState([]);
  const [searchedPokemonNames, setSearchedPokemonNames] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [fetchCount, setFetchCount] = useState(parseInt(localStorage.getItem('fetch_count')) || 0);
  const [showInfo, setShowInfo] = useState(false);
  const [squareColors, setSquareColors] = useState([]);
  const [showAnimation, setShowAnimation] = useState(false);
  const [submitButton, setSubmitButton] = useState(false);
  const [randomPokemon, setRandomPokemon] = useState(() => getPokemonFromStorage());

  useEffect(() => {
    if (!randomPokemon) {
      removeAndFetch(setRandomPokemon);
    }
    checkPokemonChange();
  }, [randomPokemon]);

  const {
    hour, minutes, dateString, dayName,
  } = getDate();

  // la funcion handleSubmit es la que se ejecuta cuando se apreta submit en el input
  const handleSubmit = async (e) => {
    e.preventDefault();

    const { query } = e.target.elements;
    const pokemonName = query.value.trim().toLowerCase();

    if (!pokemonName || searchedPokemonNames.includes(pokemonName)) {
      return;
    }

    setSubmitButton(true);

    try {
      const response = await fetch(
        `https://pokeapi.co/api/v2/pokemon/${query.value.toLowerCase()}`,
      );
      if (response.ok) {
        const data = await response.json();

        const getType = (index) => {
          const type = data.types[index]?.type.name;
          return type ? type.charAt(0).toUpperCase() + type.slice(1) : undefined;
        };

        const getAbilities = (index) => {
          const ability = data.abilities[index];
          return ability
            ? ability.ability.name.charAt(0).toUpperCase()
                + ability.ability.name.slice(1).replace('-', ' ')
            : null;
        };

        const type1 = getType(0);
        const type2 = getType(1);
        const weight = data.weight / 10;
        const height = data.height / 10;
        const gen = getGeneration(data.id);
        const total_stats = data.stats.reduce((acc, stat) => acc + stat.base_stat, 0);
        const ability1 = getAbilities(0);
        const ability2 = getAbilities(1);
        const ability3 = getAbilities(2);

        const pokemonInfo = {
          id: data.id,
          name: data.name.charAt(0).toUpperCase() + data.name.slice(1),
          sprite: data.sprites.front_default,
          gen,
          weight,
          height,
          type1,
          type2,
          total_stats,
          ability1,
          ability2,
          ability3,
        };

        pokemonInfo.colorGeneration = compareGeneration(gen, randomPokemon.gen);
        pokemonInfo.colorTypes = compareBothTypes(
          type1,
          type2,
          randomPokemon.type1,
          randomPokemon.type2,
        );
        pokemonInfo.colorAbilities = compareAbilities(
          ability1,
          ability2,
          ability3,
          randomPokemon.ability1,
          randomPokemon.ability2,
          randomPokemon.ability3,
        );
        pokemonInfo.colorWeight = compareWeight(weight, randomPokemon.weight);
        pokemonInfo.colorHeight = compareHeight(height, randomPokemon.height);
        pokemonInfo.colorTotalStats = compareTotalStats(total_stats, randomPokemon.total_stats);
        pokemonInfo.arrowWeight = returnArrow(weight, randomPokemon.weight);
        pokemonInfo.arrowHeight = returnArrow(height, randomPokemon.height);
        pokemonInfo.arrowTotalStats = returnArrow(total_stats, randomPokemon.total_stats);

        pokemonInfo.squareColors = [
          pokemonInfo.colorGeneration,
          pokemonInfo.colorTypes,
          pokemonInfo.colorWeight,
          pokemonInfo.colorHeight,
          pokemonInfo.colorTotalStats,
        ];
        squareColors.push(pokemonInfo.squareColors);
        // Actualizar el historial de pokemon en el almacenamiento local
        const storedPokemonInfoList = JSON.parse(localStorage.getItem('pokemonInfoList')) || [];
        const lastPokemon = storedPokemonInfoList[storedPokemonInfoList.length - 1];
        if (!lastPokemon || lastPokemon.id !== pokemonInfo.id) {
          storedPokemonInfoList.push(pokemonInfo);
        }
        localStorage.setItem('pokemonInfoList', JSON.stringify(storedPokemonInfoList));
        setPokemonInfoList((prevState) => [pokemonInfo, ...prevState]);
        setSearchedPokemonNames((prevState) => [...prevState, pokemonName]);
        setInputValue('');
        const storedSquareColors = JSON.parse(localStorage.getItem('square_colors')) || [];
        storedSquareColors.push(pokemonInfo.squareColors);
        localStorage.setItem('square_colors', JSON.stringify(storedSquareColors));
        // Actualizar el contador de fetch en el almacenamiento local
        const updateFetchCount = fetchCount + 1;
        setFetchCount(updateFetchCount);
        localStorage.setItem('fetch_count', JSON.stringify(updateFetchCount));

        setTimeout(() => {
          localStorage.setItem('showed_text', true);
          setShowInfo(true);
        }, 2000);

        setShowAnimation(true);

        if (pokemonInfo.name === randomPokemon.name) {
          const storedGuesses = JSON.parse(localStorage.getItem('guesses')) || {};
          storedGuesses[pokemonInfo.name] = {
            date: dateString,
            hour,
            day: dayName,
            minute: minutes,
          };
          localStorage.setItem('guesses', JSON.stringify(storedGuesses));
        }
      } else {
        throw new Error('Error de solicitud');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitButton(false);
    }
  };

  useEffect(() => {
    const pokemonInfoList = getPokemonInfoFromStorage();
    const showInfo = JSON.parse(localStorage.getItem('showed_text'));
    const showAnimation = JSON.parse(localStorage.getItem('showed_text'));
    if (pokemonInfoList !== null) {
      pokemonInfoList.reverse();
      setPokemonInfoList(pokemonInfoList);
    }
    if (showInfo !== null) {
      setShowInfo(showInfo);
    }
    if (showAnimation !== null) {
      setShowAnimation(showAnimation);
    }
  }, []);

  const autoCompleteOptions = Object.keys(Pkmn)
    .map((key) => Pkmn[key])
    .flat()
    .filter((pokemonName) => !searchedPokemonNames.includes(pokemonName));

  return (
    <>
      <PokemonAutocomplete
        options={autoCompleteOptions}
        value={inputValue}
        onSubmit={handleSubmit}
        onChange={(e, value) => setInputValue(value)}
        submitButtonDisabled={submitButton}
        loadingSubmit={submitButton}
      />
      {/* InfoText */}

      <Box>
        {showAnimation && <InfoText />}

        {/* POKEMON INFO */}

        {pokemonInfoList.map((pokemonInfo) => (
          <PokemonInfo key={pokemonInfo.id} pokemonInfo={pokemonInfo} fetchCount={fetchCount} />
        ))}
      </Box>

      {/* WINNER LETTER */}
      {pokemonInfoList.map((pokemonInfo) => (
        <WinnerLetter key={pokemonInfo.id} pokemonInfo={pokemonInfo} />
      ))}
    </>
  );
}

export default InputPokemons;
